/**
 * QuoteResponseEmbedPage - Presentation Layer
 *
 * Full-page quote response interface for C-Market embed access via JWT token.
 *
 * Flow:
 * 1. Verify JWT token from URL using shared useEmbedAuth hook
 * 2. Fetch quote request data with quoteRequestId
 * 3. Batch fetch supplier prices for all quote items
 * 4. Render full-page BaseQuoteModal
 *
 * URL: /embed/quote-response/:quoteRequestId?token=JWT&unitPrice=1000
 *
 * Clean Architecture:
 * - Uses shared useEmbedAuth hook for token verification
 * - Uses shared EmbedAuthStates for consistent UI
 * - Uses presentation hook (useQuoteSubmission)
 * - Delegates business logic to Application Service
 * - Passes domain models to BaseQuoteModal
 */

import { useParams, useSearchParams } from "react-router-dom";
import { useExternalQuoteRequestsControllerFindOne } from "@semo/api-contracts";
import {
  useVendorAuth,
  EmbedLoadingState,
  EmbedErrorState,
} from "../features/auth";
import { BaseQuoteModal } from "../components/modals/BaseQuoteModal";
import {
  useQuoteSubmission,
  type QuoteItem,
  type QuoteFormFile,
  type QuoteVatMode,
} from "../features/quotes";
import { useSupplierPrices } from "../features/quotes/presentation/hooks/useSupplierPrices";
import {
  isEmbeddedInIframe,
  notifyParentEmbedClose,
} from "../utils/embedMessaging";
import {
  isQuoteRequestClosed,
  QUOTE_REQUEST_CLOSED_MESSAGE,
} from "../features/quote-requests/domain/quoteRequestClosed";

export function QuoteResponseEmbedPage() {
  const { quoteRequestId } = useParams<{ quoteRequestId: string }>();
  const [searchParams] = useSearchParams();
  const unitPriceParam = searchParams.get("unitPrice");
  const initialUnitPrice = unitPriceParam
    ? parseFloat(unitPriceParam)
    : undefined;

  // Unified embed auth using shared hook
  const { currentUser, supplierInfo } = useVendorAuth();

  // Use external supplierId (C-Market ID) if available, otherwise fallback to UUID
  // Backend items-v2 API expects the external ID for transaction lookups
  const supplierId = supplierInfo?.supplierId || currentUser?.id;

  // Fetch quote request data (only when authenticated)
  const {
    data: quoteRequestData,
    isLoading: requestLoading,
    error: requestError,
  } = useExternalQuoteRequestsControllerFindOne(quoteRequestId || "", {
    query: {
      enabled: !!quoteRequestId,
      staleTime: 1000 * 60 * 5,
    },
  });

  // Use presentation hook for submission (Clean Architecture)
  const { handleSubmit: submitQuote, isSubmitting } = useQuoteSubmission(
    quoteRequestId || "",
  );

  // Batch fetch supplier prices for all quote items
  const { priceMap, isLoading: pricesLoading } = useSupplierPrices(
    (quoteRequestData?.data?.items || []).map((item) => ({
      itemId: item.itemId,
    })),
    supplierId ?? undefined,
  );

  // Handle form submission with domain models
  const handleSubmit = async (
    items: QuoteItem[],
    notes: string,
    files: QuoteFormFile[],
    vatMode: QuoteVatMode,
  ) => {
    if (!quoteRequestId) return;

    // Presentation hook handles:
    // 1. Domain validation
    // 2. DTO conversion
    // 3. Application Service orchestration (upload + submit)
    // 4. Error classification and toasts
    //
    // Note: Do NOT catch errors here. Let them propagate so BaseQuoteModal stays open.
    // QuoteResponseApplicationService handles the error toast.
    await submitQuote(items, notes, files, vatMode);

    // 성공 시: iframe 부모(c-market 모달)에 완료 알림 → 모달 닫기 + 상세 refetch.
    // 팝업으로 열린 경우를 위해 window.close() 도 유지(iframe 에선 no-op).
    notifyParentEmbedClose("submitted");
    setTimeout(() => window.close(), 800);
  };

  // Handle close (notify parent iframe/opener)
  const handleClose = () => {
    notifyParentEmbedClose("canceled");
  };

  // Data loading states (after auth)
  if (requestLoading || pricesLoading) {
    return (
      <EmbedLoadingState
        message={
          pricesLoading
            ? "공급사 단가(공급가액) 조회 중..."
            : "견적 요청 정보 로딩 중..."
        }
      />
    );
  }

  // Request error state
  if (requestError) {
    return (
      <EmbedErrorState
        title="견적 요청을 찾을 수 없습니다"
        message="요청하신 견적 정보가 존재하지 않거나 접근 권한이 없습니다."
      />
    );
  }

  // No data state
  const quoteRequest = quoteRequestData?.data;
  if (!quoteRequest) {
    return (
      <EmbedErrorState
        title="데이터 없음"
        message="견적 요청 데이터를 불러올 수 없습니다."
      />
    );
  }

  // 마감된 공고는 견적서 작성 화면을 열지 않는다(V-10·A3-09). 알림톡 링크로 늦게 들어와도 같다.
  if (isQuoteRequestClosed(quoteRequest)) {
    return (
      <EmbedErrorState
        title="마감된 견적 요청입니다"
        message={QUOTE_REQUEST_CLOSED_MESSAGE}
      />
    );
  }

  // Render full-page quote modal
  return (
    // h-viewport → h-full 로 사슬을 이어야 견적서가 «화면 한 장» 이 된다.
    // min-h-screen 은 높이를 정하지 않아 안쪽 h-full 이 auto 로 풀렸고, 그래서
    // A4 한 장이 통째로 늘어나 발송 막대가 화면 두 개쯤 아래로 밀려 있었다.
    <div className="h-viewport bg-slate-100 ">
      <div className="w-full h-full">
        <BaseQuoteModal
          mode="create"
          variant="full"
          quoteRequest={quoteRequest}
          initialUnitPrice={initialUnitPrice}
          supplierInfo={supplierInfo ?? undefined}
          supplierId={supplierId ?? undefined}
          supplierPriceMap={priceMap}
          onClose={handleClose}
          // iframe(모달) 안에서는 부모(c-market 모달)가 고정 닫기 버튼을 제공하므로 자체 X 를 숨긴다.
          // 팝업(top-level)으로 열린 경우엔 그대로 노출.
          hideCloseButton={isEmbeddedInIframe()}
          onSubmit={handleSubmit}
          isSubmitting={isSubmitting || pricesLoading}
        />
      </div>
    </div>
  );
}
